'use client';

import { Calendar, MapPin, ArrowRight, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';
import React, { memo, useCallback, useMemo } from 'react';
import { ALL_EVENTS } from '@/data/events';
import SectionShell from './ui/Section';
import { SectionHeader } from './ui/SectionHeader';
import { EventItem } from '@/types/events';

const EventBadge = memo(({ type }: { type: string }) => (
  <span
    className={clsx(
      'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full',
      'bg-gradient-to-r from-[#0d273d] via-[#1a3b5c] to-[#d65c30]',
      'text-white text-xs font-montserrat font-bold tracking-wider',
      'shadow-lg shadow-black/[0.3]',
      'ring-1 ring-white/[0.15]'
    )}
  >
    <Sparkles className="w-3.5 h-3.5 text-[#ffd7c7]" />
    {type}
  </span>
));

EventBadge.displayName = 'EventBadge';

const EventMetadata = memo(({ date, city }: { date: string; city: string }) => (
  <div className="flex flex-wrap items-center gap-4 mb-5 text-base md:text-lg">
    <span className="flex items-center gap-2 text-[#C0D6DF] font-medium">
      <div className="w-8 h-8 rounded-lg bg-[#d65c30]/20 flex items-center justify-center ring-1 ring-[#d65c30]/30">
        <Calendar className="w-4 h-4 text-[#d65c30]" />
      </div>
      {date}
    </span>
    <span className="flex items-center gap-2 text-[#C0D6DF] font-medium">
      <div className="w-8 h-8 rounded-lg bg-[#d65c30]/20 flex items-center justify-center ring-1 ring-[#d65c30]/30">
        <MapPin className="w-4 h-4 text-[#d65c30]" />
      </div>
      {city}
    </span>
  </div>
));

EventMetadata.displayName = 'EventMetadata';

const EventBullets = memo(({ bullets }: { bullets: string[] }) => (
  <ul className="space-y-3 mb-8">
    {bullets.map((b, i) => (
      <li key={i} className="flex items-start gap-3 group/item">
        <div
          className="w-2 h-2 rounded-full mt-2 flex-shrink-0 
                      bg-gradient-to-br from-[#d65c30] to-[#d65c30]/70
                      ring-2 ring-[#d65c30]/30
                      group-hover/item:ring-[#d65c30]/50 transition-all"
        />
        <span className="text-[#C0D6DF]/95 font-montserrat text-sm leading-relaxed">
          {b}
        </span>
      </li>
    ))}
  </ul>
));

EventBullets.displayName = 'EventBullets';

const EventCard = memo(({ event, isSingle }: { event: EventItem; isSingle: boolean }) => {
  const handleRegisterClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      window.dispatchEvent(
        new CustomEvent('open-signup', {
          detail: { eventId: event.id, lockEvent: true },
        })
      );
    },
    [event.id]
  );

  return (
    <article className="group relative w-full">
      <div
        aria-hidden="true"
        className={clsx(
          'pointer-events-none absolute -inset-[3px] rounded-3xl',
          'bg-gradient-to-br from-[#d65c30] via-[#1a3b5c] to-[#0d273d]',
          'opacity-[0.35] blur-2xl animate-softOrangePulse'
        )}
      />

      <div
        aria-hidden="true"
        className={clsx(
          'pointer-events-none absolute -inset-[4px] rounded-3xl',
          'bg-[radial-gradient(140%_140%_at_50%_0%,rgba(214,92,48,0.4),rgba(26,59,92,0.2)_50%,transparent_100%)]',
          'opacity-0 group-hover:opacity-100 transition-opacity duration-700',
          'blur-3xl'
        )}
      />

      <div
        className={clsx(
          'relative z-[1] h-full rounded-3xl overflow-hidden',
          'bg-gradient-to-br from-[#102f49]/95 via-[#0d273d]/90 to-[#1a3b5c]/95',
          'backdrop-blur-xl',
          'border border-[#C0D6DF]/20',
          'shadow-[0_20px_60px_-15px_rgba(214,92,48,0.3),0_8px_20px_-8px_rgba(13,39,61,0.6)]',
          'transition-all duration-500 group-hover:scale-[1.01] group-hover:shadow-[0_30px_80px_-20px_rgba(214,92,48,0.5)]',
          'flex flex-col'
        )}
      >
        <div className="h-1 bg-gradient-to-r from-[#d65c30] via-[#d65c30]/80 to-[#C0D6DF]/60" />

        {/* GRID LAYOUT: Content + Image */}
        <div className="grid md:grid-cols-[minmax(0,1.35fr)_minmax(0,360px)] gap-0">
          {/* LEFT SIDE: Content */}
          <div className="p-6 md:p-8 lg:p-12 flex flex-col">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <EventBadge type={event.type} />
            </div>

            <h3 className="font-montserrat font-extrabold mb-4 leading-tight text-3xl sm:text-4xl lg:text-5xl bg-gradient-to-r from-white via-[#C0D6DF] to-white bg-clip-text text-transparent">
              {event.title}
            </h3>

            <EventMetadata date={event.date} city={event.city} />

            {event.intro && (
              <p className="font-montserrat mb-4 font-semibold text-[#d65c30]/95 text-base sm:text-lg">
                {event.intro}
              </p>
            )}

            {event.description && (
              <p className="font-montserrat text-[#C0D6DF]/90 leading-relaxed mb-6 text-sm sm:text-base flex-1">
                {event.description}
              </p>
            )}

            {event.bullets && <EventBullets bullets={event.bullets} />}

            <button
              type="button"
              className={clsx(
                'group/btn relative inline-flex items-center justify-center gap-3 w-full px-6 py-4 rounded-2xl',
                'font-montserrat font-bold text-white text-base',
                'bg-gradient-to-r from-[#d65c30] to-[#d65c30]/90',
                'shadow-lg shadow-[#d65c30]/30',
                'ring-1 ring-white/20',
                'transition-all duration-300',
                'hover:scale-[1.03] hover:shadow-2xl hover:shadow-[#d65c30]/50',
                'active:scale-[0.98]',
                'mt-auto'
              )}
              onClick={handleRegisterClick}
            >
              <span
                className="absolute -inset-1 bg-gradient-to-r from-[#d65c30] via-orange-500 to-[#d65c30] 
                           blur-xl rounded-2xl animate-buttonGlow"
              />

              <span className="relative z-10 flex items-center gap-3">
                Register now
                <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform duration-300" />
              </span>
            </button>
          </div>

          {/* RIGHT SIDE: Speaker Image */}
          <div className="relative md:border-l border-[#C0D6DF]/10 order-first md:order-last">
            <div className="relative h-[360px] sm:h-[420px] md:h-full md:min-h-[420px] lg:min-h-[520px] overflow-hidden">
              {event.imageUrl ? (
                <img
                  src={event.imageUrl}
                  alt={event.imageAlt || event.speakerName || 'Event speaker'}
                  className="w-full h-full object-cover object-top md:object-center"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#1a3b5c] to-[#0d273d] flex items-center justify-center">
                  <Sparkles className="w-16 h-16 text-[#d65c30]/30" />
                </div>
              )}

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#020914]/60 via-transparent to-transparent" />

              {(event.speakerName ||
                event.speakerTitle ||
                (event.speakerHighlights && event.speakerHighlights.length > 0)) && (
                  <div className="absolute inset-x-0 bottom-0 z-20">
                    <div className="bg-gradient-to-t from-[#020914]/95 via-[#020914]/80 to-transparent px-5 py-6 sm:px-7 sm:py-7">
                      {/* SPEAKER BADGE */}
                      <div
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                                 bg-gradient-to-r from-[#0d273d] via-[#1a3b5c] to-[#d65c30]
                                 shadow-md shadow-black/40 ring-1 ring-white/20 mb-4"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-[#ffd7c7]" />
                        <span className="text-white text-xs font-montserrat font-bold tracking-wide">
                          SPEAKER
                        </span>
                      </div>

                      {event.speakerName && (
                        <p className="text-white font-montserrat font-semibold text-xl sm:text-2xl leading-snug">
                          {event.speakerName}
                        </p>
                      )}

                      {event.speakerTitle && (
                        <p className="text-[#C0D6DF] font-montserrat text-sm sm:text-base mt-1 mb-4 leading-relaxed">
                          {event.speakerTitle}
                        </p>
                      )}

                      {event.speakerHighlights && event.speakerHighlights.length > 0 && (
                        <ul className="space-y-2">
                          {event.speakerHighlights.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="mt-[7px] w-2.5 h-2.5 rounded-full bg-[#d65c30]" />
                              <span className="text-[#C0D6DF]/95 text-sm font-montserrat leading-snug">
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
});

EventCard.displayName = 'EventCard';

const MemoizedSectionHeader = memo(SectionHeader);

export default function UpcomingEventsSection() {
  const isSingleEvent = useMemo(() => ALL_EVENTS.length === 1, [ALL_EVENTS.length]);

  return (
    <SectionShell id="events" bgClass="bg-navy-bottom" withContainer>
      <style>
        {`
          @keyframes softOrangePulse {
            0%, 100% {
              opacity: .35;
              transform: scale(1) translateZ(0);
              filter: blur(32px);
            }
            50% {
              opacity: .55;
              transform: scale(1.05) translateZ(0);
              filter: blur(40px);
            }
          }

          @keyframes buttonGlow {
            0%, 100% {
              opacity: .4;
              filter: blur(16px);
            }
            50% {
              opacity: .75;
              filter: blur(20px);
            }
          }

          .animate-softOrangePulse {
            animation: softOrangePulse 8s cubic-bezier(.4,0,.2,1) infinite;
            will-change: opacity, transform, filter;
          }

          .animate-buttonGlow {
            animation: buttonGlow 3s cubic-bezier(.4,0,.6,1) infinite;
            will-change: opacity, filter;
          }
          
          .group:hover {
            will-change: transform;
          }
        `}
      </style>

      <MemoizedSectionHeader
        eyebrowText="Upcoming Events"
        eyebrowIcon={Sparkles}
        title="Events That Matter"
        subtitle="Various formats. One goal — growth through knowledge and the right acquaintances."
        textColorClass="text-white"
        align="center"
        titleGradientClassName="bg-gradient-to-r from-[#0d273d] via-[#1a3b5c] to-[#d65c30]"
      />

      <div
        className={clsx(
          'mx-auto gap-6 lg:gap-8',
          isSingleEvent ? 'w-full' : 'max-w-7xl grid grid-cols-1 md:grid-cols-2'
        )}
      >
        {ALL_EVENTS.map((ev) => (
          <EventCard key={ev.id} event={ev} isSingle={isSingleEvent} />
        ))}
      </div>
    </SectionShell>
  );
}
